import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Send, MessageSquare, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function Messages() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const endRef = useRef(null);

    const load = async () => {
        try { const { data } = await api.get("/messages"); setItems(data); }
        catch (err) { console.error("messages load failed:", err); }
    };
    useEffect(() => { load(); }, []);
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setSending(true);
        try {
            const { data } = await api.post("/messages", { body: text });
            setItems((m) => [...m, data]);
            setText("");
        } catch (err) {
            console.error("send message failed:", err);
        }
        setSending(false);
    };

    const draftAiReply = async () => {
        setDrafting(true);
        try {
            const persona = user?.role === "admin" ? "studio" : "client";
            const { data } = await api.post("/ai/draft-reply", { persona });
            setText(data.draft || "");
            toast.success("AI drafted a reply — edit if you like, then send.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "AI draft failed";
            toast.error(msg);
        }
        setDrafting(false);
    };

    return (
        <div className="max-w-3xl mx-auto h-[calc(100vh-200px)] lg:h-[calc(100vh-120px)] flex flex-col">
            <div className="mb-4 flex items-end justify-between">
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-rose">Direct line</p>
                    <h1 className="font-serif text-4xl mt-2">Talk to the studio</h1>
                </div>
                <span className="text-xs px-3 py-1.5 rounded-full bg-rose/10 border border-rose/30 text-rose">
                    30-min response guarantee
                </span>
            </div>

            <div className="flex-1 rounded-3xl border border-white/10 bg-surface p-6 overflow-y-auto luxe-scroll" data-testid="messages-list">
                {items.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center">
                        <MessageSquare className="w-10 h-10 text-rose/50" />
                        <p className="mt-4 font-serif text-2xl">No messages yet</p>
                        <p className="mt-1 text-white/50 text-sm">Send your first message — design inquiries, scheduling, anything.</p>
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {items.map((m) => (
                            <li key={m.message_id} data-testid={`msg-${m.message_id}`} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                                    m.sender === "client" ? "bg-rose text-ink rounded-br-sm" : "bg-white/8 text-white rounded-bl-sm"
                                }`}>
                                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.body}</p>
                                    <p className={`text-[10px] mt-1 ${m.sender === "client" ? "text-ink/60" : "text-white/40"}`}>
                                        {format(new Date(m.created_at), "MMM d · HH:mm")}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
                <div ref={endRef} />
            </div>

            <form onSubmit={send} className="mt-4 flex gap-3 items-center">
                <button
                    type="button"
                    onClick={draftAiReply}
                    disabled={drafting || items.length === 0}
                    data-testid="messages-ai-draft"
                    title="Suggest a reply with AI"
                    className="shrink-0 px-4 py-3 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition disabled:opacity-40 flex items-center gap-2 text-sm"
                >
                    {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    <span className="hidden sm:inline">Draft with AI</span>
                </button>
                <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Write a message…"
                    data-testid="messages-input"
                    className="flex-1 bg-surface border border-white/10 focus:border-rose rounded-full px-5 py-3 outline-none transition"
                />
                <button
                    type="submit"
                    disabled={sending || !text.trim()}
                    data-testid="messages-send"
                    className="px-5 py-3 rounded-full bg-rose text-ink hover:bg-roseHover transition disabled:opacity-50 flex items-center gap-2"
                >
                    <Send className="w-4 h-4" /> Send
                </button>
            </form>
        </div>
    );
}
